import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import TagSelect from './TagSelect';
import { PLUS } from '../icons';
import { ancestorsOf, parentOf } from '../../utils/collectionFamily';
import { normalizeId } from '../../utils/ids';
import {
	canApplyByHand,
	carrierCollectionIds,
	handTagIdsOf,
	privateApplicationsOf,
} from '../../utils/itemTags';
import { tagIdOf } from '../../utils/tags';

export const ADD_TAG_LABEL = 'Add tag';
export const PRIVATE_USE_NOTE = 'Only you see this';

/**
 * "+ ADD TAG" on one item (docs/plans/tags.md §5.2): the dashed chip at the
 * end of the item's chip row. Clicked, it becomes the tag picker
 * (TagSelect, as an applier) in its place, open: collection tags as their
 * tree, my private tags, and item tags found by title. Picking puts a tag
 * on; picking an applied one again takes it off; closing it returns the
 * chip.
 *
 * Where each pick goes — `onToggle({ kind: 'hand' | 'private', id, on, tag,
 * name, title })`:
 *   - a collection tag → by hand (`tagIds`), which needs write on the item;
 *     a restricted one only for managers of its collection, a strict rule's
 *     never (greyed, saying why — utils/itemTags.js canApplyByHand);
 *   - a private tag → privately (read is enough);
 *   - an item tag → by hand when the reader may write the item AND the tag's
 *     item lives in this item's collection or above it (its family); else
 *     privately, and the option says "Only you see this" before it is picked
 *     (ruling q2).
 *
 * `vocabulary` (the item's collection's tags) and `privateTags` (mine) come
 * from the caller, which already holds them for the chip row. `open` /
 * `onOpenChange` let the ⋯ "Tag…" on the form open it from outside.
 */
export default function ItemTagPicker({
	item,
	collection = null,
	canEdit = false,
	vocabulary = [],
	privateTags = null,
	onToggle,
	disabled = false,
	open: openProp,
	onOpenChange,
	label = ADD_TAG_LABEL,
	className = '',
}) {
	const [openState, setOpenState] = useState(false);
	const controlled = typeof openProp === 'boolean';
	const open = controlled ? openProp : openState;
	const setOpen = useCallback(next => {
		if (!controlled) setOpenState(next);
		onOpenChange?.(next);
	}, [controlled, onOpenChange]);

	const itemCollectionId = normalizeId(item?.collection_id) || normalizeId(collection?._id) || null;
	const family = useMemo(() => carrierCollectionIds(itemCollectionId, collection), [itemCollectionId, collection]);
	const handIds = useMemo(() => handTagIdsOf(item), [item]);
	const privateIds = useMemo(() => privateApplicationsOf(item).map(entry => normalizeId(entry.tagId)), [item]);
	const applied = useMemo(() => [...new Set([...handIds, ...privateIds])], [handIds, privateIds]);

	// An item tag goes on by hand only inside its item's family, by someone
	// who may write this item; anywhere else it is a private use.
	const itemTagGoesPrivate = useCallback((tag, result) => {
		const home = normalizeId(result?.collection?._id) || normalizeId(tag?.collection_id);
		return !canEdit || !home || !family.has(home);
	}, [canEdit, family]);

	const disabledFor = useCallback((tag, kind) => {
		if (kind !== 'collection') return null;
		return canApplyByHand(tag, { canEdit, collection }).reason;
	}, [canEdit, collection]);

	const noteFor = useCallback((tag, kind, result) => (
		kind === 'item' && itemTagGoesPrivate(tag, result) ? PRIVATE_USE_NOTE : null
	), [itemTagGoesPrivate]);

	const ownerLabel = useCallback(ownerId => {
		const above = ancestorsOf(collection).find(ancestor => ancestor._id === ownerId) || (parentOf(collection)?._id === ownerId ? parentOf(collection) : null);
		return above ? `From ${above.name}` : null;
	}, [collection]);

	const titlesOf = tag => ({ [tagIdOf(tag)]: { title: tag.title || '', item_id: tag.item_id || null, card_id: tag.card_id || null, ...(tag.collection_id ? { collection_id: tag.collection_id } : {}) } });

	const toggle = (id, meta, on) => {
		if (typeof onToggle !== 'function' || !meta?.tag) return;
		const { kind, tag, result } = meta;
		if (kind === 'private') {
			onToggle({ kind: 'private', id, on, tag, name: tag.name });
			return;
		}
		if (kind === 'item') {
			// Taking one off takes off the application the item has.
			const privately = on ? itemTagGoesPrivate(tag, result) : !handIds.includes(id);
			onToggle({ kind: privately ? 'private' : 'hand', itemTag: true, id, on, tag, titles: titlesOf(tag) });
			return;
		}
		onToggle({ kind: 'hand', id, on, tag });
	};

	// Esc closes the picker, not the page's form or pane under it.
	const wrapRef = useRef(null);
	useEffect(() => {
		if (!open) return undefined;
		const onKey = event => {
			if (event.key !== 'Escape') return;
			if (!wrapRef.current?.contains(event.target)) return;
			event.stopPropagation();
			setOpen(false);
		};
		document.addEventListener('keydown', onKey, true);
		return () => document.removeEventListener('keydown', onKey, true);
	}, [open, setOpen]);

	if (!open) {
		return (
			<button
				type="button"
				className={['tag-add', className].filter(Boolean).join(' ')}
				onClick={() => setOpen(true)}
				disabled={disabled}
				data-testid="tag-add"
			>
				<PLUS aria-hidden="true" />
				<span>{label}</span>
			</button>
		);
	}

	return (
		<span className="tag-add__picker" ref={wrapRef}>
			<TagSelect
				multiple
				hideSelected
				value={applied}
				collectionTags={vocabulary}
				privateTags={Array.isArray(privateTags) ? privateTags : []}
				itemTags
				disabledFor={disabledFor}
				noteFor={noteFor}
				ownerLabel={ownerLabel}
				onSelect={(id, meta) => toggle(id, meta, true)}
				onDeselect={(id, meta) => toggle(id, meta, false)}
				placeholder="Find a tag"
				aria-label="Add tag"
				autoFocus
				// Held open while it is in use: a click on its own box (to
				// type) must not close it. It closes when focus leaves it,
				// or on Esc (above).
				open
				onBlur={() => setOpen(false)}
				disabled={disabled}
				className="tag-add__select"
			/>
		</span>
	);
}
